import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import RoadSign from "@/components/RoadSign";
import { ArrowRight, ShieldCheck, BookOpen, Trophy, MapPin } from "lucide-react";

const COUNTRY_IMG = {
  FR: "https://images.unsplash.com/photo-1594796515439-da2faa37737f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxyb2FkJTIwdHJpcCUyMGZyYW5jZXxlbnwwfHx8fDE3ODgwODIwODZ8MA&ixlib=rb-4.1.0&q=85",
  CH: "https://images.unsplash.com/photo-1628062203973-88811f7fb705?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxkcml2aW5nJTIwc3dpc3MlMjBhbHBzfGVufDB8fHx8MTc4ODA4MjA4Nnww&ixlib=rb-4.1.0&q=85",
  IT: "https://images.unsplash.com/photo-1533656338503-b22f63e96cd8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxkcml2aW5nJTIwYW1hbGZpJTIwY29hc3QlMjBpdGFseXxlbnwwfHx8fDE3ODgwODIwODZ8MA&ixlib=rb-4.1.0&q=85",
};

export default function Landing() {
  const [countries, setCountries] = useState([]);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => { api.get("/countries").then(({ data }) => setCountries(data)).catch(() => {}); }, []);

  const features = [
    { icon: BookOpen, t: t("landing.f1t"), d: t("landing.f1d") },
    { icon: ShieldCheck, t: t("landing.f2t"), d: t("landing.f2d") },
    { icon: Trophy, t: t("landing.f3t"), d: t("landing.f3d") },
  ];

  return (
    <div className="paper-noise">
      <section className="max-w-7xl mx-auto px-5 pt-16 pb-10 grid lg:grid-cols-12 gap-8 items-center">
        <motion.div className="lg:col-span-6" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="text-xs tracking-[0.25em] uppercase font-semibold text-[#E06D53]">{t("landing.badge")}</span>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tighter leading-[1.05] text-[#2C2A28] mt-4">
            {t("landing.title1")}<br />{t("landing.title2")}
          </h1>
          <p className="text-base md:text-lg text-[#5C5854] mt-6 max-w-xl leading-relaxed">{t("landing.desc")}</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Button onClick={() => navigate("/inscription")} className="rounded-full h-12 px-7 bg-[#E06D53] hover:bg-[#C95B42] text-white text-base" data-testid="hero-cta-register">
              {t("landing.ctaRegister")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={() => navigate("/pays")} variant="outline" className="rounded-full h-12 px-7 border-[#E5E0D8] text-base" data-testid="hero-cta-explore">
              {t("landing.ctaExplore")}
            </Button>
          </div>
          <div className="flex items-center gap-6 mt-10">
            {[["3", t("landing.statCountries")], ["46", t("landing.statSigns")], ["20", t("landing.statQuestions")]].map(([n, l]) => (
              <div key={l}>
                <div className="font-serif text-3xl text-[#1D4E89]">{n}</div>
                <div className="text-xs uppercase tracking-widest text-[#8C857B]">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div className="lg:col-span-6 grid grid-cols-2 gap-4" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}>
          <div className="col-span-2 rounded-2xl overflow-hidden h-56 relative shadow-[0_12px_32px_rgba(44,42,40,0.12)]">
            <img src={COUNTRY_IMG.FR} alt="France" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-4 left-4 text-white font-serif text-3xl">🇫🇷 France</div>
          </div>
          <div className="rounded-2xl overflow-hidden h-44 relative shadow-md">
            <img src={COUNTRY_IMG.CH} alt="Suisse" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-3 left-3 text-white font-serif text-2xl">🇨🇭 Suisse</div>
          </div>
          <div className="rounded-2xl overflow-hidden h-44 relative shadow-md">
            <img src={COUNTRY_IMG.IT} alt="Italie" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-3 left-3 text-white font-serif text-2xl">🇮🇹 Italie</div>
          </div>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-8">
        <div className="bg-white rounded-2xl border border-[#E5E0D8] p-8 flex flex-wrap items-center justify-center gap-8">
          {[["speed-limit", "130"], ["stop", ""], ["yield", ""], ["priority-road", ""], ["roundabout", ""], ["motorway", ""], ["pedestrian-crossing", ""], ["animals", ""]].map(([tp, v], i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <RoadSign type={tp} value={v} country="FR" size={64} />
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-16">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#2C2A28] mb-10">{t("landing.featuresTitle")}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div key={i} className="bg-white rounded-2xl border border-[#E5E0D8] p-8 tc-card-hover"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <span className="w-12 h-12 rounded-full bg-[#FBEDE9] flex items-center justify-center mb-5">
                <f.icon className="w-6 h-6 text-[#E06D53]" />
              </span>
              <h3 className="text-xl font-medium text-[#2C2A28] mb-2">{f.t}</h3>
              <p className="text-[#5C5854] leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-8">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#2C2A28] mb-10">{t("landing.countriesTitle")}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {countries.map((c, i) => (
            <motion.div key={c.code} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <Link to={`/pays/${c.code}`} className="block bg-white rounded-2xl border border-[#E5E0D8] overflow-hidden tc-card-hover" data-testid={`country-card-${c.code}`}>
                <div className="h-40 relative">
                  <img src={COUNTRY_IMG[c.code]} alt={c.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{c.flag}</span>
                    <h3 className="text-xl font-medium text-[#2C2A28]">{c.name}</h3>
                  </div>
                  <p className="text-sm text-[#5C5854] leading-relaxed">{c.tagline}</p>
                  <span className="inline-flex items-center gap-1 text-[#E06D53] text-sm font-medium mt-4">
                    <MapPin className="w-4 h-4" /> {t("landing.discover")}
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="rounded-2xl bg-[#1D4E89] text-white p-12 text-center relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[#E06D53]/30 blur-3xl" />
          <h2 className="font-serif text-4xl tracking-tight mb-4">{t("landing.ctaTitle")}</h2>
          <p className="text-white/80 max-w-lg mx-auto mb-8">{t("landing.ctaDesc")}</p>
          <Button onClick={() => navigate("/tarifs")} className="rounded-full h-12 px-8 bg-[#E06D53] hover:bg-[#C95B42] text-white text-base" data-testid="cta-pricing">
            {t("landing.ctaPricing")}
          </Button>
        </div>
      </section>
    </div>
  );
}
